import { Component, inject } from '@angular/core';
import { ImportService } from '../../core/services/import.service';
import { ExcelImportPreview, ExcelImportResult, ImportError } from '../../core/models/import.model';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-import',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTableModule,
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
    DecimalPipe
  ],
  template: `
    <div class="page-header">
      <div>
        <h1 class="page-title">Import iz Excel-a</h1>
        <p class="page-subtitle">Ucitajte Excel fajl sa podacima o dobavljacima i dugovanjima</p>
      </div>
    </div>

    <!-- Upload Section -->
    <mat-card class="upload-card" *ngIf="!preview && !result">
      <div class="upload-zone"
           [class.dragover]="isDragover"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onDrop($event)">
        <mat-icon class="upload-icon">cloud_upload</mat-icon>
        <h3>Prevucite Excel fajl ovde</h3>
        <p>ili</p>
        <input type="file" #fileInput accept=".xlsx" (change)="onFileSelected($event)" style="display: none;">
        <button mat-raised-button color="primary" (click)="fileInput.click()">
          <mat-icon>folder_open</mat-icon>
          Izaberite fajl
        </button>
        <p class="file-hint">Podrzani format: .xlsx</p>
      </div>
    </mat-card>

    <!-- Loading -->
    <div class="loading-container" *ngIf="loading">
      <mat-spinner diameter="40"></mat-spinner>
      <p>{{ loadingMessage }}</p>
    </div>

    <!-- Preview Section -->
    <div *ngIf="preview && !result && !loading">
      <mat-card class="preview-card" [class.has-errors]="!preview.canImport">
        <mat-card-header>
          <mat-card-title>
            <mat-icon [class.error-icon]="!preview.canImport">{{ preview.canImport ? 'check_circle' : 'error' }}</mat-icon>
            Pregled podataka
          </mat-card-title>
          <mat-card-subtitle>{{ selectedFile?.name }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <!-- Summary Stats -->
          <div class="preview-stats">
            <div class="stat-item">
              <div class="stat-value">{{ preview.supplierSheets }}</div>
              <div class="stat-label">Dobavljaca</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ preview.validRows }}</div>
              <div class="stat-label">Validnih redova</div>
            </div>
            <div class="stat-item" [class.error-stat]="preview.invalidRows > 0">
              <div class="stat-value">{{ preview.invalidRows }}</div>
              <div class="stat-label">Nevalidnih redova</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ newSuppliersCount }}</div>
              <div class="stat-label">Novih dobavljaca</div>
            </div>
          </div>

          <!-- Totals from Excel -->
          <div class="totals-section">
            <h4>Zbirovi iz Excel fajla:</h4>
            <div class="totals-grid">
              <div class="total-item">
                <span class="total-label">Ukupan iznos:</span>
                <span class="total-value">{{ preview.previewTotalAmount | number:'1.2-2' }} RSD</span>
              </div>
              <div class="total-item">
                <span class="total-label">Ukupno placeno:</span>
                <span class="total-value">{{ preview.previewTotalPaid | number:'1.2-2' }} RSD</span>
              </div>
              <div class="total-item">
                <span class="total-label">Preostalo:</span>
                <span class="total-value highlight">{{ preview.previewTotalRemaining | number:'1.2-2' }} RSD</span>
              </div>
            </div>
          </div>

          <!-- Errors Section -->
          <div class="errors-section" *ngIf="preview.errors.length > 0">
            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon class="error-icon">error</mat-icon>
                  Greske ({{ preview.errors.length }}) - Import nije moguc!
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="error-list">
                <div class="error-item" *ngFor="let e of getGroupedErrors()">
                  <div class="error-header">
                    <strong>{{ e.sheetName }}</strong> - Red {{ e.rowNumber }}
                  </div>
                  <div class="error-message">{{ e.message }}</div>
                </div>
                <p *ngIf="preview.errors.length > 100" class="more-items">
                  ...i jos {{ preview.errors.length - 100 }} gresaka
                </p>
              </div>
            </mat-expansion-panel>
          </div>

          <!-- Suppliers List -->
          <mat-expansion-panel class="suppliers-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>Lista dobavljaca ({{ preview.suppliers.length }})</mat-panel-title>
            </mat-expansion-panel-header>
            <table mat-table [dataSource]="preview.suppliers" class="preview-table">
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef>Naziv</th>
                <td mat-cell *matCellDef="let s">
                  {{ s.name }}
                  <mat-icon *ngIf="!s.totalsMatch" class="mismatch-icon"
                            [matTooltip]="s.totalsMismatchMessage">warning</mat-icon>
                </td>
              </ng-container>
              <ng-container matColumnDef="validRows">
                <th mat-header-cell *matHeaderCellDef>Redova</th>
                <td mat-cell *matCellDef="let s">{{ s.validRows }}</td>
              </ng-container>
              <ng-container matColumnDef="totalAmount">
                <th mat-header-cell *matHeaderCellDef>Iznos</th>
                <td mat-cell *matCellDef="let s" [class.mismatch-cell]="s.excelTotalAmount && s.totalAmount !== s.excelTotalAmount">
                  {{ s.totalAmount | number:'1.2-2' }}
                </td>
              </ng-container>
              <ng-container matColumnDef="totalPaid">
                <th mat-header-cell *matHeaderCellDef>Placeno</th>
                <td mat-cell *matCellDef="let s" [class.mismatch-cell]="s.excelTotalPaid && s.totalPaid !== s.excelTotalPaid">
                  {{ s.totalPaid | number:'1.2-2' }}
                </td>
              </ng-container>
              <ng-container matColumnDef="totalRemaining">
                <th mat-header-cell *matHeaderCellDef>Preostalo</th>
                <td mat-cell *matCellDef="let s" [class.mismatch-cell]="s.excelTotalRemaining && s.totalRemaining !== s.excelTotalRemaining">
                  {{ s.totalRemaining | number:'1.2-2' }}
                </td>
              </ng-container>
              <ng-container matColumnDef="isNew">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let s">
                  <span class="status-chip" [ngClass]="s.isNew ? 'new' : 'existing'">
                    {{ s.isNew ? 'Novi' : 'Postojeci' }}
                  </span>
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="['name', 'validRows', 'totalAmount', 'totalPaid', 'totalRemaining', 'isNew']"></tr>
              <tr mat-row *matRowDef="let row; columns: ['name', 'validRows', 'totalAmount', 'totalPaid', 'totalRemaining', 'isNew'];" [class.mismatch-row]="!row.totalsMatch"></tr>
            </table>
          </mat-expansion-panel>

          <!-- Warnings Section -->
          <div class="warnings-section" *ngIf="preview.warnings.length">
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon class="warning-icon">warning</mat-icon>
                  Upozorenja ({{ preview.warnings.length }})
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="warning-item" *ngFor="let w of preview.warnings.slice(0, 50)">
                <mat-icon>warning</mat-icon>
                <span><strong>{{ w.sheetName }}</strong> (red {{ w.rowNumber }}): {{ w.message }}</span>
              </div>
              <p *ngIf="preview.warnings.length > 50" class="more-items">
                ...i jos {{ preview.warnings.length - 50 }} upozorenja
              </p>
            </mat-expansion-panel>
          </div>
        </mat-card-content>
        <mat-card-actions align="end">
          <button mat-stroked-button (click)="reset()">Odustani</button>
          <button mat-raised-button color="warn" (click)="doClearAndImport()"
                  [disabled]="!preview.canImport"
                  matTooltip="Brise sve postojece podatke i importuje ponovo">
            <mat-icon>delete_sweep</mat-icon>
            Obrisi sve i importuj
          </button>
          <button mat-raised-button color="primary" (click)="doImport()"
                  [disabled]="!preview.canImport"
                  matTooltip="Dodaje nove podatke uz postojece">
            <mat-icon>upload</mat-icon>
            Importuj
          </button>
        </mat-card-actions>
      </mat-card>
    </div>

    <!-- Result Section -->
    <div *ngIf="result && !loading">
      <mat-card class="result-card" [ngClass]="result.success ? 'success' : 'error'">
        <mat-card-header>
          <mat-icon mat-card-avatar [ngClass]="result.success ? 'success-icon' : 'error-icon'">
            {{ result.success ? 'check_circle' : 'error' }}
          </mat-icon>
          <mat-card-title>{{ result.success ? 'Import uspesan!' : 'Import zavrsen sa greskama' }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="result-stats">
            <div class="stat-item">
              <div class="stat-value">{{ result.suppliersCreated }}</div>
              <div class="stat-label">Novih dobavljaca</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ result.suppliersUpdated }}</div>
              <div class="stat-label">Azuriranih dobavljaca</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ result.payablesImported }}</div>
              <div class="stat-label">Importovanih dugovanja</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ result.payablesSkipped }}</div>
              <div class="stat-label">Preskocenih (duplikati)</div>
            </div>
          </div>

          <!-- Import Totals -->
          <div class="totals-section" *ngIf="result.success">
            <h4>Importovani zbirovi:</h4>
            <div class="totals-grid">
              <div class="total-item">
                <span class="total-label">Ukupan iznos:</span>
                <span class="total-value">{{ result.totalAmount | number:'1.2-2' }} RSD</span>
              </div>
              <div class="total-item">
                <span class="total-label">Ukupno placeno:</span>
                <span class="total-value">{{ result.totalPaidAmount | number:'1.2-2' }} RSD</span>
              </div>
              <div class="total-item">
                <span class="total-label">Preostalo:</span>
                <span class="total-value highlight">{{ result.totalRemainingAmount | number:'1.2-2' }} RSD</span>
              </div>
            </div>
          </div>

          <div class="warnings-section" *ngIf="result.warnings.length">
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>Upozorenja ({{ result.warnings.length }})</mat-panel-title>
              </mat-expansion-panel-header>
              <div class="warning-item" *ngFor="let w of result.warnings.slice(0, 50)">
                <mat-icon>warning</mat-icon>
                <span><strong>{{ w.sheetName }}</strong> (red {{ w.rowNumber }}): {{ w.message }}</span>
              </div>
              <p *ngIf="result.warnings.length > 50" class="more-items">
                ...i jos {{ result.warnings.length - 50 }} upozorenja
              </p>
            </mat-expansion-panel>
          </div>

          <div class="errors-section" *ngIf="result.errors.length">
            <h4>Greske</h4>
            <div class="error-item" *ngFor="let e of result.errors.slice(0, 20)">
              <mat-icon>error</mat-icon>
              <span><strong>{{ e.sheetName }}</strong> (red {{ e.rowNumber }}): {{ e.message }}</span>
            </div>
          </div>
        </mat-card-content>
        <mat-card-actions align="end">
          <button mat-raised-button color="primary" (click)="reset()">
            <mat-icon>refresh</mat-icon>
            Novi import
          </button>
        </mat-card-actions>
      </mat-card>
    </div>
  `,
  styles: [`
    .page-header { margin-bottom: 24px; }
    .page-title { margin: 0; font-size: 28px; font-weight: 600; color: #1F2937; }
    .page-subtitle { margin: 4px 0 0 0; color: #6B7280; font-size: 14px; }
    .upload-card { max-width: 600px; margin: 0 auto; }
    .upload-zone { border: 2px dashed #D1D5DB; border-radius: 12px; padding: 60px 40px; text-align: center; transition: all 0.3s ease; background: #F9FAFB; }
    .upload-zone.dragover { border-color: #3B82F6; background: #EFF6FF; }
    .upload-icon { font-size: 64px; width: 64px; height: 64px; color: #9CA3AF; margin-bottom: 16px; }
    .upload-zone h3 { margin: 0 0 8px 0; color: #374151; }
    .upload-zone p { margin: 0 0 16px 0; color: #6B7280; }
    .file-hint { margin-top: 16px !important; font-size: 12px; color: #9CA3AF; }
    .loading-container { display: flex; flex-direction: column; align-items: center; padding: 60px; gap: 16px; }
    .loading-container p { color: #6B7280; }
    .preview-card, .result-card { max-width: 900px; margin: 0 auto; }
    .preview-card.has-errors { border-left: 4px solid #DC2626; }
    .preview-card mat-card-title { display: flex; align-items: center; gap: 8px; }
    .preview-card mat-card-title mat-icon { color: #10B981; }
    .preview-card mat-card-title mat-icon.error-icon { color: #DC2626; }
    .preview-stats, .result-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    @media (max-width: 600px) { .preview-stats, .result-stats { grid-template-columns: repeat(2, 1fr); } }
    .stat-item { text-align: center; padding: 16px; background: #F3F4F6; border-radius: 8px; }
    .stat-item.error-stat { background: #FEE2E2; }
    .stat-value { font-size: 28px; font-weight: 700; color: #1F2937; }
    .stat-item.error-stat .stat-value { color: #DC2626; }
    .stat-label { font-size: 12px; color: #6B7280; margin-top: 4px; }
    .totals-section { background: #F0FDF4; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
    .totals-section h4 { margin: 0 0 12px 0; color: #166534; }
    .totals-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .total-item { display: flex; flex-direction: column; }
    .total-label { font-size: 12px; color: #6B7280; }
    .total-value { font-size: 18px; font-weight: 600; color: #1F2937; }
    .total-value.highlight { color: #059669; }
    .suppliers-panel { margin-bottom: 16px; }
    .preview-table { width: 100%; }
    .error-cell { color: #DC2626; font-weight: 600; }
    .status-chip { padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
    .status-chip.new { background: #DBEAFE; color: #1D4ED8; }
    .status-chip.existing { background: #F3F4F6; color: #6B7280; }
    .errors-section { margin-bottom: 16px; }
    .errors-section mat-panel-title { display: flex; align-items: center; gap: 8px; color: #DC2626; }
    .error-list { max-height: 400px; overflow-y: auto; }
    .error-item { padding: 8px 12px; background: #FEE2E2; border-radius: 4px; margin-bottom: 4px; }
    .error-header { font-size: 13px; color: #991B1B; }
    .error-message { font-size: 12px; color: #7F1D1D; }
    .error-icon { color: #DC2626 !important; }
    .warnings-section { margin-top: 16px; }
    .warnings-section mat-panel-title { display: flex; align-items: center; gap: 8px; }
    .warning-icon { color: #D97706 !important; }
    .warning-item { display: flex; align-items: flex-start; gap: 8px; padding: 8px; background: #FEF3C7; border-radius: 4px; margin-bottom: 4px; font-size: 13px; }
    .warning-item mat-icon { color: #D97706; font-size: 18px; width: 18px; height: 18px; }
    .more-items { font-size: 12px; color: #6B7280; text-align: center; margin-top: 8px; }
    .result-card.success { border-left: 4px solid #10B981; }
    .result-card.error { border-left: 4px solid #DC2626; }
    .success-icon { color: #10B981 !important; font-size: 40px !important; width: 40px !important; height: 40px !important; }
    .mismatch-icon { color: #D97706; font-size: 16px; width: 16px; height: 16px; vertical-align: middle; margin-left: 4px; }
    .mismatch-cell { color: #D97706; font-weight: 600; }
    .mismatch-row { background-color: #FEF3C7 !important; }
  `]
})
export class ImportComponent {
  private service = inject(ImportService);
  private snack = inject(MatSnackBar);

  selectedFile: File | null = null;
  preview: ExcelImportPreview | null = null;
  result: ExcelImportResult | null = null;
  loading = false;
  loadingMessage = '';
  isDragover = false;

  get newSuppliersCount(): number {
    return this.preview?.suppliers.filter(s => s.isNew).length || 0;
  }

  getGroupedErrors(): ImportError[] {
    return this.preview?.errors.slice(0, 100) || [];
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragover = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragover = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragover = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  handleFile(file: File) {
    if (!file.name.endsWith('.xlsx')) {
      this.snack.open('Samo .xlsx fajlovi su podrzani', 'Zatvori', { duration: 3000 });
      return;
    }
    this.selectedFile = file;
    this.loading = true;
    this.loadingMessage = 'Analiziranje fajla...';

    this.service.preview(file).subscribe({
      next: (preview) => {
        this.preview = preview;
        this.loading = false;
        if (!preview.canImport) {
          this.snack.open(`Pronadjeno ${preview.errors.length} gresaka - import nije moguc`, 'Zatvori', { duration: 5000 });
        }
      },
      error: (e) => {
        this.snack.open(e?.error?.message || 'Greska pri citanju fajla', 'Zatvori', { duration: 3000 });
        this.loading = false;
        this.reset();
      }
    });
  }

  doImport() {
    if (!this.selectedFile || !this.preview?.canImport) return;
    this.loading = true;
    this.loadingMessage = 'Importovanje podataka...';

    this.service.import(this.selectedFile).subscribe({
      next: (result) => {
        this.result = result;
        this.preview = null;
        this.loading = false;
        if (result.success) {
          this.snack.open(`Uspesno importovano ${result.payablesImported} dugovanja`, 'Zatvori', { duration: 3000 });
        }
      },
      error: (e) => {
        this.snack.open(e?.error?.message || 'Greska pri importu', 'Zatvori', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  doClearAndImport() {
    if (!this.selectedFile || !this.preview?.canImport) return;
    if (!confirm('Da li ste sigurni da zelite da obrisete SVE postojece podatke o dobavljacima i dugovanjima i importujete ponovo?')) {
      return;
    }
    this.loading = true;
    this.loadingMessage = 'Brisanje postojecih podataka i importovanje...';

    this.service.clearAndImport(this.selectedFile).subscribe({
      next: (result) => {
        this.result = result;
        this.preview = null;
        this.loading = false;
        if (result.success) {
          this.snack.open(`Uspesno importovano ${result.payablesImported} dugovanja`, 'Zatvori', { duration: 3000 });
        }
      },
      error: (e) => {
        this.snack.open(e?.error?.message || 'Greska pri importu', 'Zatvori', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  reset() {
    this.selectedFile = null;
    this.preview = null;
    this.result = null;
  }
}
